<template>
  <div class="flexible-popup intake-form-overlay reveal" id="intakeFormOverlay" data-reveal>
    <a href="javascript:void(0);" class="close-reveal-modal" data-close aria-label="Close">&#215;</a>
    <form data-form-name="sdn_first_intake_form" id="intake-form">
      <h2>Let's Personalize Your Tuition Discounts</h2>
      <p>
        Please answer a few quick questions so we can provide you with the best scholarship and discount matches.
      </p>
      <p class="margin-bottom-2">
        <strong>NOTE:</strong>
        This information can be updated in your Profile.
      </p>

      <!-- Type of degree program block -->
      <h4>
        What type of degree program are you interested in?
      </h4>
      <div class="custom-select flat-select f-form">
        <div class="arrow-select"></div>
        <select v-model="degreeType" data-field-name="degree_program" id="degreeType" :class="{ 'placeholder-selected': degreeType == null }">
          <option :value="null" disabled hidden v-show="!degreeType">Select one to continue</option>
          <option
            v-for="program in degreePrograms"
            :value="program">{{program}}</option>
        </select>
      </div>

      <p class="skippy-sharp-message" v-if="userDegreeType">
        <i class="fa-regular fa-arrow-turn-left-up"></i>
        This is what you told us when you first joined Sophia. Update it here if your interests have changed.
      </p>

      <div v-if="showRestOfFormButton">
        <a
            href="javascript:void(0);"
            @click="onShowRestOfFormClicked"
            class="w-button purple"
        >
          Continue
        </a> &nbsp;
      </div>
      <transition name="slide">
        <div v-if="!showRestOfFormButton">
          <!-- Military affiliation block -->
          <transition name="slide">
            <div class="choose-military-affiliation" v-show="showHaveMilitaryAffiliation">
              <h4> Do you have any military affiliation? </h4>
              <div class="military-affiliation">
                <div class="button-group">
                  <input
                    v-for="option in militaryAffiliationOptions"
                    type="radio"
                    :id="option.id"
                    :ref="option.id"
                    :name="option.name"
                    :value="option.value"
                    data-field-name="military_affiliation"
                    class="hidden"
                    v-model="militaryAffiliation"
                  >
                  <label
                    v-for="option in militaryAffiliationOptions"
                    tabindex="0"
                    @keyup.enter="onRadioEnter(option.value, 'militaryAffiliation')"
                    :class="['w-button', 'small', 'margin-right-1', getLabelClass(option.value, militaryAffiliation)]"
                    :for="option.id"
                  >
                    {{ option.label }}
                  </label>
                </div>
              </div>
            </div>
          </transition>

          <!-- Content that is shown when Yes or No is clicked -->
          <transition name="slide">
            <div v-show="showMilitaryAffiliationBlock" class="intake-form-block" >
              <!-- How are you affiliated block -->
              <transition name="slide">
                <div
                  v-show="showMilitaryAffiliationDetails"
                  class="military-status"
                >
                  <p class="margin-0">How are you affiliated?</p>

                  <ul class="radio-container">
                    <li v-for="item in militaryStatus">
                      <input :id="formatValue(item)"
                             :name="formatValue(item)"
                             :value="item"
                             type="radio"
                             data-field-name="how_are_you_affiliated"
                             :checked="item === militaryAffiliationDetails && militaryAffiliation === 'true'"
                             v-model="militaryAffiliationDetails"
                      />
                      <label :for="formatValue(item)">{{item}}</label>
                    </li>
                  </ul>
                </div>
              </transition>

              <!-- Type of school block -->
              <transition name="slide">
                <div class="school-type" v-show="showDegreeType">
                  <h4>What type of school are you looking for?</h4>

                  <div>
                    <p class="title-message">Degree type</p>
                    <input
                      v-for="option in degreeOptions"
                      type="radio"
                      :id="formatValue(option) + '_degree_type'"
                      :name="formatValue(option)"
                      :value="option"
                      data-field-name="degree_type"
                      class="hidden"
                      v-model="lookingForDegreeType"
                    >
                    <label
                      v-for="option in degreeOptions"
                      tabindex="0"
                      @keyup.enter="onRadioEnter(option, 'lookingForDegreeType')"
                      :class="['w-button', 'small', 'margin-right-1', getLabelClass(option, lookingForDegreeType)]"
                      :for="formatValue(option) + '_degree_type'"
                    >
                      {{ option }}
                    </label>
                  </div>
                </div>
              </transition>
            </div>
          </transition>

          <!-- Course modality block  -->
          <transition name="slide">
            <div class="intake-form-block course-modality" v-show="showCourseModality">
              <p class="title-message">Course modality</p>
              <input
                v-for="option in courseModality"
                type="radio"
                :id="formatValue(option) + '_modality'"
                :name="formatValue(option)"
                :value="option"
                data-field-name="course_modality"
                class="hidden"
                v-model="lookingForCourseModality"
              >
              <label
                v-for="option in courseModality"
                tabindex="0"
                @keyup.enter="onRadioEnter(option, 'lookingForCourseModality')"
                :class="['w-button', 'small', 'margin-right-1', getLabelClass(option, lookingForCourseModality)]"
                :for="formatValue(option) + '_modality'"
              >
                {{ option }}
              </label>
              <div class="hang-in-container">
                <div class="hang-in-image">
                  <img :src="imageUrl" title="Hang in there!">
                </div>
                <div class="hang-in-message">
                  <p>Hang in there!</p>
                  <p>Just a few more questions!</p>
                </div>
              </div>
            </div>
          </transition>

          <!-- Region block  -->
          <transition name="slide">
            <div class="choose-region" v-show="showChooseRegion">
              <p class="margin-0">Region</p>

              <ul class="radio-container">
                <li v-for="item in regions">
                  <input :id="formatValue(item)"
                         :name="formatValue(item)"
                         :value="item"
                         type="radio"
                         data-field-name="region"
                         v-model="lookingForRegion"
                  />
                  <label :for="formatValue(item)">{{item}}</label>
                </li>
              </ul>
            </div>
          </transition>

          <!-- School size block -->
          <transition name="slide">
            <div class="intake-form-block school-size-block" v-show="showSchoolSize">
              <p class="title-message">School size</p>
              <input
                v-for="option in schoolSize"
                type="radio"
                :id="formatValue(option) + '_schoolsize'"
                :name="formatValue(option)"
                :value="option"
                data-field-name="school_size"
                class="hidden"
                v-model="lookingForSchoolSize"
              >
              <label
                v-for="option in schoolSize"
                tabindex="0"
                @keyup.enter="onRadioEnter(option, 'lookingForSchoolSize')"
                :class="['w-button', 'small', 'margin-right-1', getLabelClass(option, lookingForSchoolSize)]"
                :for="formatValue(option) + '_schoolsize'"
              >
                {{ option }}
              </label>
            </div>
          </transition>

          <!-- HBCU block -->
          <transition name="slide">
            <div class="intake-form-block hbcu-block" v-show="showHBCU">
              <p class="title-message">Are you interested in attending a Historically Black College or University (HBCU)?</p>
              <input
                v-for="item in hbcuOptions"
                type="radio"
                :id="formatValue(item) + '_HBCU'"
                :name="formatValue(item)"
                :value="item"
                data-field-name="interested_in_HBCU"
                class="hidden"
                v-model="interestedInHbcu"
              >
              <label
                v-for="item in hbcuOptions"
                tabindex="0"
                @keyup.enter="onRadioEnter(item, 'interestedInHbcu')"
                :class="['w-button small margin-right-1', getLabelClass(item, interestedInHbcu)]"
                :for="formatValue(item) + '_HBCU'"
              >
                {{ item }}
              </label>
            </div>
          </transition>

          <!-- Agree checkbox and buttons -->
          <transition name="slide">
            <div class="terms-agree" v-show="showSdnAgree">
              <div class="intake-form-block">
                <p>By participating in the Scholarships and Discounts Network, I consent to Sophia sharing my information, including but not limited to my name, email address, phone number, zip code, degree program of interest, and number of Sophia courses completed, with participating institutions. I understand that the purpose of sharing this information is to facilitate communication and engagement regarding scholarship opportunities, tuition discounts, and other relevant offerings from participating institutions. I understand that my information will be shared in accordance with Sophia's Privacy Policy, which outlines how my information is collected, used, disclosed, and protected by Sophia.</p>
                <ul class="checkbox-container">
                  <li>
                    <input type="checkbox" id="agreeCheckbox" v-model="sdnAgree">
                    <label for="agreeCheckbox">I agree to these terms</label>
                  </li>
                </ul>
                <a
                  href="javascript:void(0);"
                  @click="onContinueClick"
                  :class="['w-button purple', { 'disabled': !sdnAgree }]"
                >
                  Continue
                </a> &nbsp;
                <a href="javascript:void(0);" @click="onClosePopup" class="w-button white cancel-button">Cancel</a>
              </div>
            </div>
          </transition>
        </div>
      </transition>
    </form>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'
  import { kebabCase } from 'lodash'
  import { updatePersonalInformationProfile } from '../api/api.js'
  import $ from 'jquery'

  export default {
    props: {
      imageUrl: {
        type: String
      },
      scholarPath: {
        type: String
      },
      profileCompleted: {
        type: Boolean
      },
      userInfoMilitaryAffiliation: {
        type: String
      },
      userInfoMilitaryAffiliationDetails: {
        type: String
      }
    },
    data() {
      return {
        degreeType: null,
        militaryAffiliation: this.userInfoMilitaryAffiliation,
        militaryAffiliationDetails: this.userInfoMilitaryAffiliationDetails,
        militaryAffiliationOptions: [
          { value: 'false', id: 'no-military', name: 'no-military', label: 'No' },
          { value: 'true', id: 'yes-military', name: 'yes-military', label: 'Yes' }
        ],
        lookingForDegreeType: null,
        lookingForCourseModality: null,
        lookingForRegion: null,
        lookingForSchoolSize: null,
        interestedInHbcu: null,
        sdnAgree: this.userSdnAgree,
        restOfFormIsShown: false
      }
    },
    computed: {
      ...mapGetters(['degreePrograms', 'userDegreeType', 'militaryStatus', 'degreeOptions', 'courseModality', 'regions', 'schoolSize', 'hbcuOptions', 'userSdnAgree']),

      showHaveMilitaryAffiliation() {
        return this.degreeType;
      },

      showMilitaryAffiliationBlock() {
        return this.militaryAffiliation && this.degreeType;
      },
      showMilitaryAffiliationDetails() {
        return this.militaryAffiliation == 'true'
      },
      showDegreeType() {
        return this.militaryAffiliation == 'false' || this.militaryAffiliationDetails != null
      },
      showCourseModality() {
        return this.lookingForDegreeType != null
      },
      showChooseRegion() {
        const modalities = ["In-Person", "Hybrid"];
        return modalities.includes(this.lookingForCourseModality);
      },
      showSchoolSize() {
        return this.lookingForCourseModality == "Online" || this.lookingForCourseModality == "Unsure" || this.lookingForRegion != null
      },
      showHBCU() {
        return this.lookingForSchoolSize != null
      },
      showSdnAgree() {
        return this.interestedInHbcu != null
      },
      showRestOfFormButton() {
        return this.profileCompleted && this.userDegreeType && !this.restOfFormIsShown;
      }
    },
    methods: {
      ...mapActions(['setUserSdnAgree']),
      formatValue(value) {
        return kebabCase(value)
      },
      getLabelClass(value, model) {
        return {
          'purple': model === value,
          'white-btn': model != value
        };
      },
      onClosePopup() {
        $('.intake-form-overlay').foundation('close')
      },
      onContinueClick(e) {
        if (!this.sdnAgree) return;

        const data = {
          user: {
            degree_type: this.degreeType,
            military_affiliation: this.militaryAffiliation,
            military_affiliation_details: this.militaryAffiliationDetails,
            looking_for_degree_type: this.lookingForDegreeType,
            looking_for_course_modality: this.lookingForCourseModality,
            looking_for_region: this.lookingForRegion,
            looking_for_school_size: this.lookingForSchoolSize,
            interested_in_hbcu: this.interestedInHbcu,
            sdn_agree: this.sdnAgree
          }
        }
        updatePersonalInformationProfile(data).then(() => {
          this.onClosePopup()
          SOPHIA.formGA($('#intake-form'), 'form_submit')
          this.sendSnowplowEvent()
          this.setUserSdnAgree(true)
          window.open(this.scholarPath, '_blank')
          SOPHIA.clickModalCloseForGA($(e.target))
        })
      },
      sendSnowplowEvent() {
        snowplow('trackStructEvent', {
          category: 'sdn_first_intake_form',
          action: 'submit'
        });
      },
      onRadioEnter(option, model) {
        if (this.$data[model] === option) {
          this.$data[model] = null;
        } else {
          this.$data[model] = option;
        }
      },
      onShowRestOfFormClicked() {
        this.restOfFormIsShown = true;
      }
    },
    mounted() {
      this.degreeType = this.userDegreeType || null;
      this.sdnAgree = this.userSdnAgree;
    },
    watch: {
      militaryAffiliation() {
        // Reset values when switch military affiliation tabs
        this.militaryAffiliationDetails = null
        this.lookingForDegreeType = null
        this.lookingForCourseModality = null
        this.lookingForRegion = null
        this.lookingForSchoolSize =  null
        this.interestedInHbcu = null
        this.sdnAgree = false
      },
      lookingForCourseModality() {
        // Reset values when switch course modality tabs
        this.lookingForRegion = null
        this.lookingForSchoolSize = null
        this.interestedInHbcu = null
        this.sdnAgree = false
      }
    }
  }
</script>

